import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FunnelIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-toastify';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';

const DAY_SHORT = {
  sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat',
};

const StandingOrders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [pagination, setPagination]     = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  useEffect(() => {
    fetchOrders();
  }, [searchParams]);

  const fetchOrders = async () => {
    try {
      const page   = searchParams.get('page') || 1;
      const status = searchParams.get('status') || statusFilter;
      const params = new URLSearchParams({ page, limit: 20, ...(status && { status }) });
      const response = await api.get(`/admin/standing-orders?${params}`);
      setOrders(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch standing orders');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('page', '1');
    setSearchParams(params);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Standing Orders</h1>
          <p className="text-sm text-gray-500 mt-1">Recurring committed ride series</p>
        </div>
        <span className="text-gray-500">{pagination.total} total orders</span>
      </div>

      {/* Filters */}
      <div className="card">
        <form onSubmit={handleFilter} className="flex flex-col sm:flex-row gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input sm:w-48"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <button type="submit" className="btn-primary">
            <FunnelIcon className="w-5 h-5 mr-2 inline" />
            Filter
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Order #</th>
                <th className="table-header">Rider</th>
                <th className="table-header">Driver</th>
                <th className="table-header">Route</th>
                <th className="table-header">Schedule</th>
                <th className="table-header">Fare</th>
                <th className="table-header">Status</th>
                <th className="table-header">Created</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.length === 0 && (
                <tr>
                  <td colSpan={9} className="table-cell text-center text-gray-400 py-8">
                    No standing orders found
                  </td>
                </tr>
              )}
              {orders.map((order) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  <td className="table-cell">
                    <span className="font-mono text-sm font-semibold">
                      SO-{order.standingOrderNumber}
                    </span>
                  </td>
                  <td className="table-cell">
                    {order.rider ? (
                      <p className="font-medium">
                        {order.rider.firstName} {order.rider.lastName}
                      </p>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="table-cell">
                    {order.committedDriver ? (
                      <div>
                        <p className="font-medium">
                          {order.committedDriver.firstName} {order.committedDriver.lastName}
                        </p>
                        {order.isSpecificDriverRequested && (
                          <span className="text-xs text-indigo-600">Requested by rider</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">Unassigned</span>
                    )}
                  </td>
                  <td className="table-cell">
                    <div className="max-w-xs">
                      <p className="text-sm truncate">{order.pickup?.address}</p>
                      <p className="text-xs text-gray-500 truncate">
                        → {order.dropoff?.address}
                      </p>
                    </div>
                  </td>
                  <td className="table-cell">
                    <p className="text-sm">
                      {(order.days || []).map((d) => DAY_SHORT[d] || d).join(', ')}
                    </p>
                    <p className="text-xs text-gray-500">{order.time}</p>
                  </td>
                  <td className="table-cell font-medium text-sm">
                    {order.quote?.totalFare != null
                      ? `${order.quote.totalFare} ${order.quote.currency || 'QAR'}`
                      : '—'}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="table-cell text-sm text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <Link
                      to={`/standing-orders/${order._id}`}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          pages={pagination.pages}
          onPageChange={(p) =>
            setSearchParams((prev) => { prev.set('page', p); return prev; })
          }
        />
      </div>
    </div>
  );
};

export default StandingOrders;
